import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import PartnerUsersTable from "./PartnerUsersTable";
import AddUserModal from "./Modals/AddUserModal";
import ActivateWalletModal from "./Modals/ActivateWalletModal";
import DeleteConfirmationModal from "./Modals/DeleteConfirmationModal";
import AssignTeamModal from "./Modals/AssignTeamModal";
import ChangeUserRoleModal from "./Modals/ChangeUserRoleModal";
import EditUserModal from "./Modals/EditUserModal";
import FixPendingUserEmailModal from "./Modals/FixPendingUserEmailModal";
import UserInfoModal from "./Modals/UserInfoModal";
import UserTransactionLimitTypeModal from "./Modals/UserTransactionLimitTypeModal";
import {
  ADMIN_LIMITS_PANEL_TOUR_ID,
  buildAdminLimitsPanelTourSteps,
} from "./Modals/UserTransactionLimitTypeModal.tour";

import { getAllPartnerTeams } from "../../services/PartnerService";
import {
  getAssignableRoles,
  getUserWalletBalance,
  deletePartnerUser,
  updatePartnerUser,
  resendPartnerUserVerification,
  fixPendingPartnerUserEmail,
  assignUserToTeam,
  changeUserRole,
} from "../../services/UserService";
import { getExpenseTags, getTransactionLimitTypes, getUserTransactionLimits, setUserTransactionLimit, revertTemporaryUserTransactionLimit, deleteUserTransactionLimit } from "../../services/TransactionService";

import { canManagePartnerDetails } from "../../utils/auth/accessRules";
import { PERMISSION_SLUGS, ROLE_SLUGS } from "../../utils/auth/permissionConstants";
import useGuidedTour from "../../hooks/useGuidedTour";
import { showMutationError, makeFieldErrorClearer } from "../../utils/showMutationError";

const emptyTransactionLimitForm = { amount_limit: "", transaction_limit_type_id: "" };

const PartnerUsersSection = ({ partnerId, authUser, userPartnerDetails, cardRef, mode = "full", onSeeAll, teamFilter = null, onClearTeamFilter, onAddExpenseTag, onAddTeam }) => {
  const queryClient = useQueryClient();

  const [currentUser, setCurrentUser] = useState(null);
  const [viewAddUserModal, setViewAddUserModal] = useState(false);
  const [viewInfoUserModal, setViewInfoUserModal] = useState(false);
  const [viewEditUserModal, setViewEditUserModal] = useState(false);
  const [viewFixEmailModal, setViewFixEmailModal] = useState(false);
  const [viewDeleteConfirmationModal, setViewDeleteConfirmationModal] = useState(false);
  const [viewActivateWalletModal, setViewActivateWalletModal] = useState(false);
  const [viewAssignTeamModal, setViewAssignTeamModal] = useState(false);
  const [viewChangeRoleModal, setViewChangeRoleModal] = useState(false);
  const [viewAddUserTransactionLimitModal, setViewAddUserTransactionLimitModal] = useState(false);

  const [assignTeamFieldErrors, setAssignTeamFieldErrors] = useState({});
  const [changeRoleFieldErrors, setChangeRoleFieldErrors] = useState({});
  const [editUserFieldErrors, setEditUserFieldErrors] = useState({});
  const [fixEmailFieldErrors, setFixEmailFieldErrors] = useState({});

  const clearAssignTeamFieldError = makeFieldErrorClearer(setAssignTeamFieldErrors);
  const clearChangeRoleFieldError = makeFieldErrorClearer(setChangeRoleFieldErrors);
  const clearEditUserFieldError = makeFieldErrorClearer(setEditUserFieldErrors);
  const clearFixEmailFieldError = makeFieldErrorClearer(setFixEmailFieldErrors);

  const [userWalletBalance, setUserWalletBalance] = useState(null);
  const [loadingWalletBalance, setLoadingWalletBalance] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  const [userTransactionLimitFormData, setUserTransactionLimitFormData] = useState(emptyTransactionLimitForm);
  const [isSubmittingTransactionLimit, setIsSubmittingTransactionLimit] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [limitToRevoke, setLimitToRevoke] = useState(null);

  const canManageUsers = canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.MANAGE_PARTNER_USERS);
  const canChangeUserRole = canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.CHANGE_USER_ROLE);
  const viewerIsSuperAdmin = authUser?.roleData?.slug === ROLE_SLUGS.SUPER_ADMIN;

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: getAssignableRoles,
    enabled: !!userPartnerDetails && canManageUsers,
    staleTime: 5 * 60 * 1000,
  });
  const roles = rolesData?.data?.data ?? null;

  const { data: allPartnerTeamsData, isLoading: allPartnerTeamsLoading } = useQuery({
    queryKey: ["allPartnerTeams", partnerId],
    queryFn: () => getAllPartnerTeams(partnerId),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const allPartnerTeams = allPartnerTeamsData?.data?.data ?? null;
  const teamsKnown = !allPartnerTeamsLoading;

  const { data: expenseTagsData, isLoading: expenseTagsLoading } = useQuery({
    queryKey: ["expenseTags", partnerId, 1],
    queryFn: () => getExpenseTags(partnerId, 1),
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
  });
  const hasExpenseTags = (expenseTagsData?.data?.data?.length ?? 0) > 0;
  const expenseTagsKnown = !expenseTagsLoading;

  const { data: transactionLimitTypesData, isLoading: transactionLimitTypesLoading } = useQuery({
    queryKey: ["transactionLimitTypes"],
    queryFn: getTransactionLimitTypes,
    staleTime: 5 * 60 * 1000,
  });
  const transactionLimitTypes = transactionLimitTypesData?.data?.data ?? null;

  const {
    data: userTransactionLimitsData,
    isLoading: userTransactionLimitsLoading,
    isError: userTransactionLimitsError,
  } = useQuery({
    queryKey: ["userTransactionLimits", partnerId, currentUser?.id],
    queryFn: () => getUserTransactionLimits(partnerId, currentUser.id),
    enabled: !!currentUser?.id && viewAddUserTransactionLimitModal,
    staleTime: 0,
  });
  const userTransactionLimits = userTransactionLimitsData?.data?.data ?? null;

  const canManageThisPanel = canManagePartnerDetails(
    authUser,
    userPartnerDetails,
    PERMISSION_SLUGS.MANAGE_USER_TRANSACTION_LIMITS
  );
  const hasAnyRevokableLimit = Array.isArray(userTransactionLimits) && userTransactionLimits.length > 0;
  useGuidedTour(
    ADMIN_LIMITS_PANEL_TOUR_ID,
    buildAdminLimitsPanelTourSteps({ includeRevokeStep: hasAnyRevokableLimit }),
    {
      enabled:
        viewAddUserTransactionLimitModal &&
        canManageThisPanel &&
        !userTransactionLimitsLoading,
    }
  );

  useEffect(() => {
    if (userTransactionLimitsError) {
      showMutationError(userTransactionLimitsError, "Failed to load transaction limits. Please try again.");
    }
  }, [userTransactionLimitsError]);

  const invalidatePartnerUsers = () => {
    queryClient.invalidateQueries({ queryKey: ["partnerUsers", partnerId], exact: false });
    queryClient.invalidateQueries({ queryKey: ["partnerHasSoloUser", partnerId] });
    queryClient.invalidateQueries({ queryKey: ["activePartnerUsersCount", partnerId] });
  };
  const invalidateReviewers = () => {
    queryClient.invalidateQueries({ queryKey: ["usersWhoCanBeAssignedAsReviewers", partnerId] });
  };
  const invalidateUserTransactionLimits = () => {
    if (!currentUser?.id) return;
    queryClient.invalidateQueries({ queryKey: ["userTransactionLimits", partnerId, currentUser.id] });
  };

  const openDeleteUserModal = async (user) => {
    setCurrentUser(user);
    setUserWalletBalance(null);
    setViewDeleteConfirmationModal(true);
    setLoadingWalletBalance(true);
    try {
      const response = await getUserWalletBalance(partnerId, user.id);
      setUserWalletBalance(response.data.balance);
    } catch {
      setUserWalletBalance("0.00");
    } finally {
      setLoadingWalletBalance(false);
    }
  };

  const handleDeleteUser = async () => {
    if (deletingUser) return;
    setDeletingUser(true);
    try {
      await deletePartnerUser(partnerId, currentUser.id);
      toast.success("User deleted successfully.");
      invalidatePartnerUsers();
      setViewDeleteConfirmationModal(false);
      setCurrentUser(null);
      setUserWalletBalance(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to delete user. Please try again.");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleAssignTeam = async (teamId) => {
    setAssignTeamFieldErrors({});
    try {
      await assignUserToTeam(partnerId, currentUser.id, teamId);
      toast.success("Team assigned successfully.");
      invalidatePartnerUsers();
      setViewAssignTeamModal(false);
      setCurrentUser(null);
    } catch (err) {
      showMutationError(err, {
        fallback: "Failed to assign team. Please try again.",
        onFieldErrors: setAssignTeamFieldErrors,
      });
      throw err;
    }
  };

  const handleChangeRole = async ({ roleId, reason, currentPassword }) => {
    setChangeRoleFieldErrors({});
    try {
      await changeUserRole(partnerId, currentUser.id, {
        roleId,
        reason,
        currentPassword,
      });
      toast.success("Role updated.");
      invalidatePartnerUsers();
      setViewChangeRoleModal(false);
      setCurrentUser(null);
    } catch (err) {
      showMutationError(err, {
        fallback: "Failed to change role. Please try again.",
        onFieldErrors: setChangeRoleFieldErrors,
      });
      throw err;
    }
  };

  const handleEditUser = async (payload) => {
    setEditUserFieldErrors({});
    try {
      await updatePartnerUser(partnerId, currentUser.id, payload);
      toast.success("User updated successfully.");
      invalidatePartnerUsers();
      setViewEditUserModal(false);
      setCurrentUser(null);
    } catch (err) {
      showMutationError(err, {
        fallback: "Failed to update user. Please try again.",
        onFieldErrors: setEditUserFieldErrors,
      });
      throw err;
    }
  };

  const resendVerificationMutation = useMutation({
    mutationFn: (userId) => resendPartnerUserVerification(partnerId, userId),
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Verification pin sent.");
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Could not send the verification pin. Try again in a moment.");
    },
  });

  const fixEmailMutation = useMutation({
    mutationFn: (newEmail) => fixPendingPartnerUserEmail(partnerId, currentUser.id, newEmail),
    onSuccess: (response) => {
      toast.success(response?.data?.message || "Email corrected. A new verification pin is on its way.");
      invalidatePartnerUsers();
      setViewFixEmailModal(false);
      setCurrentUser(null);
    },
    onError: (err) => showMutationError(err, {
      fallback: "Failed to correct the email. Try again in a moment.",
      onFieldErrors: setFixEmailFieldErrors,
    }),
  });

  const setLimitMutation = useMutation({
    mutationFn: (data) => setUserTransactionLimit(partnerId, currentUser.id, data),
  });

  const revokeLimitMutation = useMutation({
    mutationFn: (limitData) =>
      limitData.type === "temporary"
        ? revertTemporaryUserTransactionLimit(partnerId, limitData.id)
        : deleteUserTransactionLimit(partnerId, limitData.id),
    onSuccess: (_, limitData) => {
      toast.success(
        limitData.type === "temporary"
          ? "The original limit has been restored."
          : "The limit has been removed."
      );
      setShowConfirmModal(false);
      invalidateUserTransactionLimits();
    },
    onError: (error, limitData) => {
      showMutationError(
        error,
        limitData.type === "temporary"
          ? "Failed to revert the limit."
          : "Failed to remove the limit."
      );
    },
    onSettled: () => {
      setIsSubmittingTransactionLimit(false);
    },
  });

  const handleTransactionLimitFormInputChange = (e) => {
    const { id, value } = e.target;
    setUserTransactionLimitFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleAddUserTransactionLimit = async () => {
    if (isSubmittingTransactionLimit) return;
    setIsSubmittingTransactionLimit(true);

    const submitAction = async () => {
      await setLimitMutation.mutateAsync(userTransactionLimitFormData);
      invalidateUserTransactionLimits();
      setUserTransactionLimitFormData(emptyTransactionLimitForm);
    };

    toast.promise(submitAction(), {
      pending: "Processing request ...",
      success: "User limit added successfully!",
      error: {
        render({ data: err }) {
          return err?.response?.data?.message || "Failed to add user limit. Please try again later.";
        },
      },
    }).finally(() => setIsSubmittingTransactionLimit(false));
  };

  const confirmRevoke = () => setShowConfirmModal(true);
  const onCancelRevoke = () => {
    setLimitToRevoke(null);
    setShowConfirmModal(false);
  };
  const onConfirmRevoke = (limitData) => {
    if (isSubmittingTransactionLimit) return;
    setIsSubmittingTransactionLimit(true);
    revokeLimitMutation.mutate(limitData);
  };

  const closeTransactionLimitModal = () => {
    setViewAddUserTransactionLimitModal(false);
    setCurrentUser(null);
    setUserTransactionLimitFormData(emptyTransactionLimitForm);
  };

  return (
    <>
      <PartnerUsersTable
        partnerId={partnerId}
        authUser={authUser}
        userPartnerDetails={userPartnerDetails}
        cardRef={cardRef}
        mode={mode}
        onSeeAll={onSeeAll}
        teamFilter={teamFilter}
        onClearTeamFilter={onClearTeamFilter}
        onAddUser={canManageUsers ? () => setViewAddUserModal(true) : undefined}
        onViewInfo={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewInfoUserModal(true);
        }}
        onManageTransactionLimits={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewAddUserTransactionLimitModal(true);
        }}
        onEditUser={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewEditUserModal(true);
        }}
        onActivateWallet={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewActivateWalletModal(true);
        }}
        onAssignTeam={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewAssignTeamModal(true);
        }}
        onChangeRole={canChangeUserRole ? (partnerUser) => {
          setCurrentUser(partnerUser);
          setViewChangeRoleModal(true);
        } : undefined}
        onDeleteUser={(partnerUser) => openDeleteUserModal(partnerUser)}
        onResendVerification={(partnerUser) => resendVerificationMutation.mutate(partnerUser.id)}
        onFixEmail={(partnerUser) => {
          setCurrentUser(partnerUser);
          setViewFixEmailModal(true);
        }}
        hasExpenseTags={hasExpenseTags}
        onAddExpenseTag={onAddExpenseTag}
      />

      <FixPendingUserEmailModal
        show={viewFixEmailModal}
        onHide={() => {
          setViewFixEmailModal(false);
          setCurrentUser(null);
          setFixEmailFieldErrors({});
        }}
        currentUser={currentUser}
        submitting={fixEmailMutation.isPending}
        onSubmit={(newEmail) => {
          setFixEmailFieldErrors({});
          fixEmailMutation.mutate(newEmail);
        }}
        serverFieldErrors={fixEmailFieldErrors}
        onClearServerFieldError={clearFixEmailFieldError}
      />

      <AddUserModal
        show={viewAddUserModal}
        onHide={() => setViewAddUserModal(false)}
        partnerId={partnerId}
        roles={roles}
        rolesLoading={rolesLoading}
        allPartnerTeams={allPartnerTeams}
        hasExpenseTags={hasExpenseTags}
        expenseTagsKnown={expenseTagsKnown}
        teamsKnown={teamsKnown}
        onAddExpenseTag={onAddExpenseTag}
        onAddTeam={onAddTeam}
        onSuccess={() => {
          invalidatePartnerUsers();
          invalidateReviewers();
        }}
      />

      <UserInfoModal
        show={viewInfoUserModal}
        onHide={() => {
          setViewInfoUserModal(false);
          setCurrentUser(null);
        }}
        currentUser={currentUser}
      />

      <UserTransactionLimitTypeModal
        show={viewAddUserTransactionLimitModal}
        onHide={closeTransactionLimitModal}
        currentUser={currentUser}
        formData={userTransactionLimitFormData}
        onInputChange={handleTransactionLimitFormInputChange}
        onSubmit={handleAddUserTransactionLimit}
        isSubmitting={isSubmittingTransactionLimit}
        isRevoking={revokeLimitMutation.isPending}
        transactionLimitTypes={transactionLimitTypes}
        transactionLimitTypesLoading={transactionLimitTypesLoading}
        existingTransactionLimits={userTransactionLimits}
        existingTransactionLimitsLoading={userTransactionLimitsLoading}
        confirmRevoke={confirmRevoke}
        showConfirmModal={showConfirmModal}
        limitToRevoke={limitToRevoke}
        setLimitToRevoke={setLimitToRevoke}
        onCancelRevoke={onCancelRevoke}
        onConfirmRevoke={onConfirmRevoke}
        canManagePartnerDetails={canManagePartnerDetails(authUser, userPartnerDetails, PERMISSION_SLUGS.MANAGE_USER_TRANSACTION_LIMITS)}
      />

      <DeleteConfirmationModal
        show={viewDeleteConfirmationModal}
        onHide={() => {
          if (!deletingUser) {
            setViewDeleteConfirmationModal(false);
            setCurrentUser(null);
            setUserWalletBalance(null);
          }
        }}
        onConfirm={handleDeleteUser}
        userName={currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : ""}
        walletBalance={userWalletBalance}
        loadingBalance={loadingWalletBalance}
        submitting={deletingUser}
      />

      <EditUserModal
        show={viewEditUserModal}
        onHide={() => {
          setViewEditUserModal(false);
          setCurrentUser(null);
          setEditUserFieldErrors({});
        }}
        currentUser={currentUser}
        partnerTeams={allPartnerTeams}
        partnerId={partnerId}
        onSuccess={handleEditUser}
        serverFieldErrors={editUserFieldErrors}
        onClearServerFieldError={clearEditUserFieldError}
      />

      <AssignTeamModal
        show={viewAssignTeamModal}
        onHide={() => {
          setViewAssignTeamModal(false);
          setCurrentUser(null);
          setAssignTeamFieldErrors({});
        }}
        serverFieldErrors={assignTeamFieldErrors}
        onClearServerFieldError={clearAssignTeamFieldError}
        currentUser={currentUser}
        partnerTeams={allPartnerTeams}
        teamsKnown={teamsKnown}
        onSubmit={handleAssignTeam}
        onAddTeam={onAddTeam}
      />

      <ChangeUserRoleModal
        show={viewChangeRoleModal}
        onHide={() => {
          setViewChangeRoleModal(false);
          setCurrentUser(null);
          setChangeRoleFieldErrors({});
        }}
        currentUser={currentUser}
        assignableRoles={roles}
        rolesKnown={!rolesLoading}
        viewerCanAssignSuperAdmin={viewerIsSuperAdmin}
        onSubmit={handleChangeRole}
        serverFieldErrors={changeRoleFieldErrors}
        onClearServerFieldError={clearChangeRoleFieldError}
      />

      <ActivateWalletModal
        show={viewActivateWalletModal}
        onHide={() => {
          setViewActivateWalletModal(false);
          setCurrentUser(null);
        }}
        partnerId={partnerId}
        currentUser={currentUser}
        allPartnerTeams={allPartnerTeams}
        hasExpenseTags={hasExpenseTags}
        expenseTagsKnown={expenseTagsKnown}
        teamsKnown={teamsKnown}
        onAddExpenseTag={onAddExpenseTag}
        onAddTeam={onAddTeam}
        onSuccess={() => {
          invalidatePartnerUsers();
        }}
      />
    </>
  );
};

export default PartnerUsersSection;
